import { AlertTriangle } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { AvailabilityProfile } from '@ooh/contracts';
import type { SearchableSelectFieldOption as SelectOption } from '@ooh/ui/components/searchable-select-field';
import { LabeledSearchableSelectField } from '@ooh/ui/components/labeled-searchable-select-field';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';

type PreviewT = TFunction<'availabilityImport'>;
import { DateFormatField, DATE_FORMAT_CUSTOM_VALUE } from './date-format-field';
import { deriveYearSpanWarning } from '../lib/derive-year-span-warning';
import { MonthPeriodColumnsTable, type MonthPeriodRow } from './month-period-columns-table';
import { GlobalManualDateRangeFields } from './global-manual-date-range-fields';
import { PeriodLabelMappingTable, type PeriodLabelMapping } from './period-label-mapping-table';
import { MarkRestUnavailableCheckbox } from './mark-rest-unavailable-checkbox';
import { StatusTokenMappingTable, type StatusTokenMappedState } from './status-token-mapping-table';
import type { UnmappedStatusToken } from '../lib/derive-status-tokens';

// Each section below renders null when its encoding/condition doesn't apply
// to the current profile — extracted from `preview-phase.tsx` so that
// component's own render function stays within the repo's per-function
// complexity/line-count lint budget (one conditional branch per section
// component instead of one big component with 5 encoding branches inline).

export function DateRangeSection({
  t,
  profile,
  dateFormatSelection,
  onDateFormatChange,
  dateFormatCustomValue,
  onDateFormatCustomChange,
}: {
  t: PreviewT;
  profile: AvailabilityProfile;
  dateFormatSelection: string;
  onDateFormatChange: (next: string) => void;
  dateFormatCustomValue: string;
  onDateFormatCustomChange: (next: string) => void;
}) {
  if (profile.timeEncoding !== 'date_range') return null;
  return (
    <DateFormatField
      options={[
        { value: 'auto', label: t('preview.dateFormatOptions.auto') },
        { value: 'dd-mm-yyyy', label: t('preview.dateFormatOptions.dd-mm-yyyy') },
        { value: 'yyyy-mm-dd', label: t('preview.dateFormatOptions.yyyy-mm-dd') },
        { value: 'excel-serial', label: t('preview.dateFormatOptions.excel-serial') },
        { value: DATE_FORMAT_CUSTOM_VALUE, label: t('preview.dateFormatOptions.custom') },
      ]}
      value={dateFormatSelection}
      onChange={onDateFormatChange}
      customValue={dateFormatCustomValue}
      onCustomValueChange={onDateFormatCustomChange}
      labels={{
        label: t('preview.dateFormatLabel'),
        customFormatPlaceholder: t('preview.dateFormatCustomPlaceholder'),
        customFormatHelperText: t('preview.dateFormatCustomHelperText'),
      }}
      testIdPrefix="preview-date-format"
    />
  );
}

export function MonthPeriodSection({
  t,
  profile,
  columnOptions,
  rows,
  onUpdateRow,
  onUpdateYear,
  isConfident,
  onAddRow,
  onRemoveRow,
}: {
  t: PreviewT;
  profile: AvailabilityProfile;
  columnOptions: SelectOption[];
  rows: MonthPeriodRow[];
  onUpdateRow: (id: string, patch: Partial<Omit<MonthPeriodRow, 'id' | 'month'>>) => void;
  onUpdateYear: (id: string, nextYear: number) => void;
  /** Distinguishes the resolver's own auto-detected month-matrix result
   * (always confident) from the operator manually switching to `month` via
   * `TimeEncodingSelector` (always low-confidence, CU-86cb1npz2 gap #2) — the
   * year column only makes sense for the former (no auto-detected month/half
   * to preserve for a manual row), and the add/remove affordances only for
   * the latter (the real auto-detected-rows call site keeps zero visible
   * change). */
  isConfident: boolean;
  onAddRow: () => void;
  onRemoveRow: (id: string) => void;
}) {
  if (profile.timeEncoding !== 'month' && profile.timeEncoding !== 'half_month') return null;
  return (
    <MonthPeriodColumnsTable
      rows={rows}
      columnOptions={columnOptions}
      onUpdateRow={onUpdateRow}
      onUpdateYear={isConfident ? onUpdateYear : undefined}
      onAddRow={isConfident ? undefined : onAddRow}
      onRemoveRow={isConfident ? undefined : onRemoveRow}
      labels={{
        sectionLabel: t('preview.monthPeriodSectionLabel'),
        periodColumnLabel: t('preview.monthPeriodPeriodColumnLabel'),
        sourceColumnLabel: t('preview.monthPeriodSourceColumnLabel'),
        dateRangeColumnLabel: t('preview.dateRangeColumnLabel'),
        yearColumnLabel: t('preview.monthPeriodYearColumnLabel'),
        dateRangePlaceholder: t('preview.dateRangePlaceholder'),
        addRowButtonLabel: t('preview.monthPeriodAddRowLabel'),
        removeRowLabel: t('preview.monthPeriodRemoveRowLabel'),
        emptyStateText: t('preview.monthPeriodEmptyStateText'),
      }}
      testIdPrefix="preview-period"
    />
  );
}

/**
 * Zero-anchor year-span review prompt (CU-12472jzd1ea): the resolver treats
 * a file with no explicit year as one continuous season anchored by its
 * first column, but that anchor is still a today-relative GUESS — unlike an
 * explicit `MM/YYYY`/inline-year header, which states its own year split.
 * Warns only in the former case; see `deriveYearSpanWarning`.
 */
export function YearSpanWarningSection({
  t,
  profile,
}: {
  t: PreviewT;
  profile: AvailabilityProfile;
}) {
  if (profile.timeEncoding !== 'month' && profile.timeEncoding !== 'half_month') return null;
  if (!deriveYearSpanWarning(profile.periodColumns)) return null;
  return (
    <InlineErrorMessage tone="warning" icon={AlertTriangle} testId="year-span-warning">
      {t('preview.yearSpanWarningText')}
    </InlineErrorMessage>
  );
}

export function StatusTokenSection({
  t,
  tokens,
  statusDictionary,
  onUpdate,
}: {
  t: PreviewT;
  tokens: UnmappedStatusToken[];
  statusDictionary: Record<string, 'FREE' | 'UNAVAILABLE'>;
  onUpdate: (rawToken: string, next: StatusTokenMappedState) => void;
}) {
  if (tokens.length === 0) return null;
  return (
    <StatusTokenMappingTable
      rows={tokens.map((token) => ({
        id: token.rawToken,
        rawToken: token.rawToken,
        occurrenceCount: token.occurrenceCount,
        mappedState: statusDictionary[token.rawToken] ?? '',
        onChange: (next: StatusTokenMappedState) => onUpdate(token.rawToken, next),
      }))}
      labels={{
        heading: t('preview.statusTokenMappingHeading'),
        helperText: t('preview.statusTokenMappingHelperText'),
        tokenHeader: t('preview.statusTokenMappingTokenHeader'),
        occurrenceHeader: t('preview.statusTokenMappingOccurrenceHeader'),
        stateHeader: t('preview.statusTokenMappingStateHeader'),
        freeLabel: t('preview.rowsStatusValues.FREE'),
        unavailableLabel: t('preview.rowsStatusValues.UNAVAILABLE'),
        statePlaceholder: t('preview.statusTokenMappingStatePlaceholder'),
        unmappedBadge: t('preview.statusTokenMappingUnmappedBadge'),
      }}
      testIdPrefix="preview-status-token"
    />
  );
}

export function GlobalRangeSection({
  t,
  profile,
  dateFrom,
  dateTo,
  onChange,
}: {
  t: PreviewT;
  profile: AvailabilityProfile;
  dateFrom: string;
  dateTo: string;
  onChange: (next: { from: string; to: string }) => void;
}) {
  if (profile.timeEncoding !== 'global' || profile.globalPeriod !== null) return null;
  return (
    <GlobalManualDateRangeFields
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={onChange}
      labels={{
        sectionLabel: t('preview.globalRangeSectionLabel'),
        dateRangePlaceholder: t('preview.dateRangePlaceholder'),
      }}
      testIdPrefix="preview-global-range"
    />
  );
}

export function PeriodLabelSection({
  t,
  profile,
  columnOptions,
  onColumnChange,
  mappings,
  onUpdate,
}: {
  t: PreviewT;
  profile: AvailabilityProfile;
  columnOptions: SelectOption[];
  onColumnChange: (next: string) => void;
  mappings: PeriodLabelMapping[];
  onUpdate: (label: string, field: 'from' | 'to', value: string) => void;
}) {
  if (profile.timeEncoding !== 'period_label_map') return null;
  return (
    <>
      <LabeledSearchableSelectField
        id="preview-period-label-column-select"
        label={t('preview.periodLabelColumnLabel')}
        options={columnOptions}
        value={profile.periodLabelColumn ?? ''}
        onChange={onColumnChange}
        placeholder={t('preview.columnPlaceholder')}
        testId="preview-period-label-column-select"
      />
      <PeriodLabelMappingTable
        mappings={mappings}
        onUpdate={onUpdate}
        labels={{
          sectionLabel: t('preview.periodLabelSectionLabel'),
          valueColumnLabel: t('preview.periodLabelValueColumnLabel'),
          dateRangeColumnLabel: t('preview.dateRangeColumnLabel'),
          dateRangePlaceholder: t('preview.dateRangePlaceholder'),
        }}
        testIdPrefix="preview-period-label"
      />
    </>
  );
}

export function MarkRestSection({
  t,
  show,
  checked,
  onCheckedChange,
  supplierName,
  count,
}: {
  t: PreviewT;
  show: boolean;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  supplierName: string;
  count: number;
}) {
  if (!show) return null;
  return (
    <MarkRestUnavailableCheckbox
      checked={checked}
      onCheckedChange={onCheckedChange}
      supplierName={supplierName}
      count={count}
      labels={{
        checkboxLabel: t('preview.markRestUnavailableLabel'),
        noteTemplate: t('preview.markRestUnavailableNoteTemplate'),
      }}
      testId="preview-mark-rest-unavailable"
    />
  );
}
